
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../App';
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { CHATS_COLLECTION } from '../config/constants';
import ChatListItem from '../components/common/ChatListItem';
import AuthLoadingScreen from './AuthLoadingScreen';
import { History, ArrowLeft } from 'lucide-react';

const CHATS_PER_PAGE = 12;

const ChatsListScreen = () => {
    const {
        db,
        user,
        activeRole,
    } = useAppContext();

    const [allChats, setAllChats] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [showHistory, setShowHistory] = useState(false);
    const [rotation, setRotation] = useState(0);
    const navigate = useNavigate();
    const listContainerRef = useRef(null);

    // CORREÇÃO CIRÚRGICA: Removendo dependências voláteis do useCallback para evitar loop infinito
    const fetchChats = useCallback(async (loadMore = false) => {
        if (!db || !user) return;

        // Se já está carregando ou se não tem mais itens no modo loadMore, ignora
        if (loadMore && (!hasMore || isLoadingMore)) return;

        if (loadMore) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
            setAllChats([]);
            setLastVisible(null);
            setHasMore(true);
        }

        try {
            let q = query(
                collection(db, CHATS_COLLECTION),
                where("participants", "array-contains", user.uid),
                orderBy('lastMessageTimestamp', 'desc'),
                limit(CHATS_PER_PAGE)
            );

            // Usa o lastVisible local do estado para a paginação
            if (loadMore && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const documentSnapshots = await getDocs(q);
            const newChats = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (documentSnapshots.docs.length > 0) {
                setLastVisible(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
            }

            setAllChats(prev => loadMore ? [...prev, ...newChats] : newChats);
            setHasMore(newChats.length === CHATS_PER_PAGE);

        } catch (error) {
            console.error("Erro ao carregar a lista de chats:", error);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [db, user]); // Apenas db e user são dependências estáveis aqui

    // Roda apenas na montagem ou quando o papel do usuário muda
    useEffect(() => {
        fetchChats(false);
    }, [activeRole, fetchChats]);

    const filteredChats = useMemo(() => {
        if (!user) return [];

        const terminalStatus = ['Finalizado', 'Cancelado', 'Pago'];

        const businessChats = allChats.filter(chat => {
            const isSupport = chat.serviId === "SYSTEM_ADMIN_SUPPORT" || chat.clientId === "SYSTEM_ADMIN_SUPPORT";
            if (isSupport) return false;

            if (activeRole === 'client') return chat.clientId === user.uid;
            if (activeRole === 'servi') return chat.serviId === user.uid;
            return false;
        });

        return businessChats.filter(chat => {
            const isWaitingPayment = chat.status === 'Aguardando Pagamento' ||
                                   (chat.lastMessage && chat.lastMessage.toLowerCase().includes('cobrança'));

            const isActuallyFinished = terminalStatus.includes(chat.status) && !isWaitingPayment;

            if (showHistory) {
                return isActuallyFinished || chat.status === 'Cancelado';
            } else {
                return !isActuallyFinished && chat.status !== 'Cancelado';
            }
        });
    }, [allChats, activeRole, user, showHistory]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop < clientHeight * 1.75 && hasMore && !isLoadingMore) {
            fetchChats(true);
        }
    };

    const handleOpenChat = (chatId) => {
        const path = activeRole === 'servi' ? `/servi/chat/${chatId}` : `/chat/${chatId}`;
        navigate(path);
    };

    const handleToggleHistory = () => {
        setShowHistory(prev => !prev);
        setRotation(prev => prev + 360);
    };

    const screenTitle = showHistory ? "Histórico de Conversas" : "Minhas Conversas";

    if (isLoading && filteredChats.length === 0) {
        return <AuthLoadingScreen message="Carregando conversas..." />;
    }

    return (
        <div className="p-4 h-full flex flex-col bg-gray-50">
            <div className="flex justify-between items-center mb-4">
                 <div className="flex items-center text-left">
                    {showHistory && (
                        <button onClick={handleToggleHistory} className="p-2 rounded-full hover:bg-gray-200 mr-2" title="Voltar para Conversas Ativas">
                            <ArrowLeft className="w-6 h-6 text-gray-700" />
                        </button>
                    )}
                    <h2 className="text-2xl font-bold text-gray-800">{screenTitle} <span className='text-lg text-gray-500'>({filteredChats.length})</span></h2>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={handleToggleHistory}
                        title={showHistory ? "Ver Conversas Ativas" : "Ver Histórico"}
                        className={`p-2 rounded-lg transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${showHistory ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 focus:ring-blue-400' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-400'}`}>
                        <History
                            className="w-5 h-5 transition-transform duration-500 ease-in-out"
                            style={{ transform: `rotate(${rotation}deg)` }}
                        />
                    </button>
                </div>
            </div>

            <div ref={listContainerRef} onScroll={handleScroll} className="flex-grow overflow-y-auto space-y-2 pb-20">
                {filteredChats.length > 0 ? (
                    filteredChats.map(chat => (
                        <ChatListItem
                            key={chat.id}
                            chat={chat}
                            currentUser={user}
                            onClick={() => handleOpenChat(chat.id)}
                        />
                    ))
                ) : (
                    <div className="text-center p-8 bg-white rounded-xl shadow-lg mt-4">
                        <p className="font-semibold text-gray-500">Nenhuma conversa {showHistory ? 'no histórico' : 'ativa'} encontrada.</p>
                    </div>
                )}
                {isLoadingMore && <div className="text-center p-4 text-sm text-gray-500 italic">Carregando mais...</div>}
            </div>
        </div>
    );
};

export default ChatsListScreen;
